import { useEffect, useRef, type ReactNode } from 'react'
import { CloseButton } from '../design'
import * as s from './sidepanel.css'

// The right-docking drawer for the Account surface. It reuses SpeciesModal's lifecycle —
// Escape closes, the scrim closes, body scroll locks via `body.modal-open` — but docks to
// the right and APPEARS with a quiet opacity fade (never a slide), so the live sparkline
// stays the only moving thing. A fixed head over a scrolling body. (Admin is a full-width
// workspace now, not a drawer — see AdminPanel / admin.css.ts.)
export function SidePanel({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  // Same lifecycle as the species sheet, including handing focus in and back out again.
  const panelRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onEsc)
    document.body.classList.add('modal-open')
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.classList.remove('modal-open')
      opener?.focus?.()
    }
  }, [onClose])

  return (
    <div className={`${s.root} is-open`}>
      <div className={s.scrim} onClick={onClose} />
      <aside
        className={s.panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className={s.head}>
          <h1 className={s.title}>{title}</h1>
          <CloseButton aria-label="Close" onClick={onClose} />
        </div>
        <div className={s.body}>{children}</div>
      </aside>
    </div>
  )
}
